// "Change plan" for an existing subscription (2026-10).
//
// Was: the CheckoutModal in change mode — opened with the USER tiers whatever
// the subscription was, and with no plan picker at all, so a Pro doctor had no
// way to reach Premium. Now one sheet lists the paid plans of the
// subscription's own entity type, priced for its interval and seat count:
//   - a pricier plan hands over to <OneClickPaymentModal action="upgrade">,
//     which previews the prorated amount charged now and confirms it;
//   - a cheaper plan is confirmed here and sent to POST /change-plan. A
//     Stripe-billed row keeps its plan until the period ends (the change is
//     scheduled); any other row changes at once and the unused part becomes
//     subscription credit.
//
// Also here: the "Changes to X on <date>" notice with its "Keep" action (the
// same route with the current plan code cancels the scheduled change), and the
// Free plan card shown to a doctor who pays for nothing.

import { useState, type ReactNode } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, CalendarClock, Check, ChevronRight, Crown, Loader2 } from "lucide-react";

import type { PricingTier } from "@/components/billing/checkout/checkout-types";
import { formatPrice } from "@/components/billing/format-price";
import {
  GoToServicePauseButton,
  isServicesPausedError,
  usePauseCarryBillingMessage,
  useServicesPausedBillingMessage,
} from "@/components/billing/services-paused-billing";
import { hasPerSeatTiers } from "@/components/pricing/clinic-seat-pricing";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadErrorState } from "@/components/ui/load-error-state";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { isVaultUnlockCancelled, useProtectedAction } from "@/lib/hooks/use-protected-action";
import { calculatePlanPrice } from "@/lib/pricing/calculate-plan-price";
import { showToast } from "@/lib/ui/toast";
import { tLocalized, type LocalizedString } from "@/types/localized-string";

const CHANGE_PLAN_PATH = "/api/account/billing/change-plan";

/** The subscription fields this file reads — a subset of the billing row. */
export interface ChangePlanSubscription {
  id: string;
  entityType: string;
  planTier: string;
  planInterval: string;
  currency: string;
  price: number;
  quantity: number;
  currentPeriodEnd: string | null;
  cancelledAt: string | null;
  /** Absent until the API ships it — the copy then stays neutral. */
  billedByStripe?: boolean;
  scheduledChange?: {
    planCode: string;
    planName: unknown;
    effectiveAt: string;
  } | null;
  plan: { code: string; name: Record<string, string> } | null;
}

interface ChangePlanResponse {
  success: boolean;
  type?: "upgrade" | "downgrade" | "same";
  scheduled?: boolean;
  effectiveDate?: string;
  subscriptionCredited?: number;
  amountCharged?: number;
  proratedAmount?: number;
  scheduledChangeCancelled?: boolean;
}

/**
 * The plans this subscription can move to, priced at its own PPP — what the
 * change charges (GET /api/account/billing/plan-options). Same key as the seat
 * sheet's preview.
 */
function usePlanOptions(subscriptionId: string, enabled: boolean) {
  const { i18n } = useLingui();
  return useQuery({
    queryKey: ["api", "/api/account/billing/plan-options", subscriptionId, i18n.locale],
    queryFn: () =>
      apiFetch.get<{ tiers: PricingTier[] }>(
        `/api/account/billing/plan-options?subscriptionId=${encodeURIComponent(subscriptionId)}&locale=${i18n.locale}`,
      ),
    enabled,
  });
}

/** The public catalog of an entity type (the Free plan card: features, no price). */
function usePricingTiers(entityType: string, enabled: boolean) {
  const { i18n } = useLingui();
  return useQuery({
    queryKey: ["api", "/api/pricing/tiers", entityType, i18n.locale],
    queryFn: () =>
      apiFetch.get<{ tiers: PricingTier[] }>(
        `/api/pricing/tiers?targetType=${encodeURIComponent(entityType)}&locale=${i18n.locale}`,
      ),
    enabled,
  });
}

/** Same date shape as the "Renews on" line of the billing page. */
function useFormatDate() {
  const { i18n } = useLingui();
  return (iso: string | null | undefined) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString(i18n.locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };
}

/**
 * Base + seats for the subscription's own quantity and interval — the shape
 * the server compares when it decides upgrade vs downgrade
 * (apps/api/src/lib/subscriptions/change-plan.ts). A clinic plan sees its
 * seat-inclusive total, a doctor plan its graduated one.
 */
function tierIntervalTotal(tier: PricingTier, quantity: number, interval: "monthly" | "annual") {
  const seatPriced = hasPerSeatTiers(tier);
  return calculatePlanPrice({
    basePriceMonthly: tier.priceMonthly,
    basePriceAnnual: tier.priceAnnual > 0 ? tier.priceAnnual : undefined,
    quantity: Math.max(1, quantity),
    interval,
    volumeTiers: tier.volumeTiers,
    providersIncluded: tier.providersIncluded ?? 1,
    seatMode: seatPriced ? "flat" : "graduated",
  }).intervalTotal;
}

function isCurrentTier(tier: PricingTier, sub: ChangePlanSubscription) {
  return !!tier.planCode && tier.planCode === (sub.plan?.code ?? sub.planTier);
}

/** Readable copy for every documented refusal of POST /change-plan. */
export function useChangePlanErrorCopy() {
  const { t } = useLingui();
  const servicesPausedMessage = useServicesPausedBillingMessage();
  const pauseCarryMessage = usePauseCarryBillingMessage();
  return (err: unknown): string => {
    if (!(err instanceof ApiError)) return t`Something went wrong. Please try again.`;
    const body = err.body as { error?: string; code?: string } | null;
    switch (body?.code) {
      case "SERVICES_PAUSED":
        return servicesPausedMessage;
      case "PAUSE_CARRY_ACTIVE":
        return pauseCarryMessage(err);
      case "plan_change_scheduled":
        return t`Cancel the scheduled plan change first.`;
      case "subscription_cancelling":
        return t`This subscription ends at the close of the billing period. Resume it before changing the plan.`;
      case "plan_misconfigured":
        return t`This plan is not set up for per-doctor billing yet. Please contact support.`;
      case "plan_not_purchasable":
        return t`This plan cannot be bought right now.`;
      case "annual_not_available_for_coverage":
        return t`Clinic coverage is billed monthly — annual is not available.`;
      case "subscription_cancelled":
        return t`This subscription is cancelled — reactivate it first.`;
      case "subscription_busy":
        return t`Another change to this subscription is in progress. Please try again in a moment.`;
      case "subscription_changed":
        return t`This subscription changed in the meantime. Reload and try again.`;
      case "subscription_lease_unavailable":
        return t`Billing is temporarily unavailable. Please try again in a moment.`;
      case "founding_upgrade_unavailable":
        return t`This change is not available on your Founding plan. Please contact support.`;
      case "payment_failed":
        return t`The payment for this change did not go through, so nothing was changed.`;
      case "payment_not_returned":
        return t`This subscription changed in the meantime and the payment could not be returned automatically. Our team will refund it — please do not try again.`;
      case "payment_processing":
        return t`A payment for this subscription is still being processed. Please try again in a moment.`;
      default:
        // A 503 with no code is Stripe not configured (with a code it is the lease store).
        if (err.status === 503) return t`Payments are not available yet. Please try again later.`;
        return body?.error || t`Something went wrong. Please try again.`;
    }
  };
}

// ---------------------------------------------------------------------------
// ChangePlanSheet
// ---------------------------------------------------------------------------

interface ChangePlanSheetProps {
  open: boolean;
  onClose: () => void;
  sub: ChangePlanSubscription;
  currentPlanName: string;
  /** A pricier plan: the caller opens the one-click upgrade (preview → confirm). */
  onUpgrade: (newPlanCode: string) => void;
  /** After a downgrade went through — the caller refreshes the billing data. */
  onChanged: () => void;
  /** 409 SERVICES_PAUSED: the caller closes the sheet and shows its pause card. */
  onShowServicePause?: () => void;
}

/** Mount it per opening (the caller renders it only while open): each opening starts from the list. */
export function ChangePlanSheet({
  open,
  onClose,
  sub,
  currentPlanName,
  onUpgrade,
  onChanged,
  onShowServicePause,
}: ChangePlanSheetProps) {
  const { t, i18n } = useLingui();
  const formatDate = useFormatDate();
  const errorCopy = useChangePlanErrorCopy();
  const { runProtected, PinGateElement } = useProtectedAction(["billing", "wallet"]);
  const [confirmTier, setConfirmTier] = useState<PricingTier | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The last refusal was SERVICES_PAUSED — the error box offers the way out.
  const [servicesPaused, setServicesPaused] = useState(false);

  const tiersQ = usePlanOptions(sub.id, open);
  const interval: "monthly" | "annual" = sub.planInterval === "annual" ? "annual" : "monthly";
  const money = (n: number) => formatPrice(n, sub.currency, i18n.locale);

  const allTiers = tiersQ.data?.tiers ?? [];
  const plans = allTiers.filter(
    (tier) => !!tier.planCode && tier.priceMonthly > 0 && tier.visibility !== "coming_soon",
  );
  // From the whole list — a hidden current plan still prices the comparison.
  // `sub.price` (which holds the add-on packs) is the last resort only; the
  // API refuses a confirmed "downgrade" that is really an upgrade
  // (`refuseUpgrade` below) and the pick goes to the upgrade preview.
  const currentTier = allTiers.find((tier) => isCurrentTier(tier, sub));
  const currentTotal = currentTier
    ? tierIntervalTotal(currentTier, sub.quantity, interval)
    : sub.price;
  // A downgrade waits for the period end: another downgrade replaces it, any
  // other move needs the current plan kept first (the API answers 409).
  const scheduledLock = !!sub.scheduledChange && !sub.cancelledAt;

  const change = useMutation({
    mutationFn: (newPlanCode: string) =>
      runProtected(() =>
        apiFetch.post<ChangePlanResponse>(CHANGE_PLAN_PATH, {
          subscriptionId: sub.id,
          newPlanCode,
          refuseUpgrade: true,
        }),
      ),
  });

  const confirmDowngrade = async () => {
    if (!confirmTier?.planCode) return;
    setError(null);
    const newPlan = confirmTier.name;
    const handle = showToast(t`Saving…`, "loading");
    try {
      const res = await change.mutateAsync(confirmTier.planCode);
      const credited = res.subscriptionCredited ?? 0;
      const date = formatDate(res.effectiveDate ?? sub.currentPeriodEnd);
      handle.update(
        res.type === "downgrade" && res.scheduled
          ? t`Saved — you move to ${newPlan} on ${date}.`
          : credited > 0
            ? t`Saved — you are now on ${newPlan}. ${money(credited)} was credited to your subscription balance.`
            : t`Saved — you are now on ${newPlan}.`,
        "success",
      );
      onChanged();
      onClose();
    } catch (err) {
      // The PIN gate was dismissed: nothing was sent.
      if (isVaultUnlockCancelled(err)) {
        handle.dismiss();
        return;
      }
      // Priced above the current plan after all: nothing was charged — the
      // upgrade preview takes over.
      if (
        err instanceof ApiError &&
        (err.body as { code?: string } | null)?.code === "upgrade_needs_confirmation"
      ) {
        handle.dismiss();
        setConfirmTier(null);
        onUpgrade(confirmTier.planCode);
        return;
      }
      const message = errorCopy(err);
      setError(message);
      setServicesPaused(isServicesPausedError(err));
      handle.update(message, "error");
    }
  };

  const pick = (tier: PricingTier, total: number) => {
    if (!tier.planCode || isCurrentTier(tier, sub)) return;
    if (total > currentTotal) {
      onUpgrade(tier.planCode);
      return;
    }
    setError(null);
    setConfirmTier(tier);
  };

  const scheduledCode = sub.scheduledChange?.planCode;
  const confirmName = confirmTier?.name ?? "";
  const periodEnd = formatDate(sub.currentPeriodEnd);

  return (
    <>
      {PinGateElement}
      <ResponsiveSheet
        open={open}
        onClose={onClose}
        title={<Trans>Change plan</Trans>}
        description={currentPlanName}
        width="md"
        footer={
          confirmTier ? (
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="min-h-[44px] flex-1"
                onClick={() => {
                  setConfirmTier(null);
                  setError(null);
                }}
                disabled={change.isPending}
              >
                <ArrowLeft className="size-4" aria-hidden />
                <Trans>Back</Trans>
              </Button>
              <Button
                className="min-h-[44px] flex-1"
                onClick={confirmDowngrade}
                disabled={change.isPending}
              >
                {change.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                <Trans>Confirm change</Trans>
              </Button>
            </div>
          ) : undefined
        }
      >
        <div className="space-y-3 p-5">
          {confirmTier ? (
            <div className="space-y-3">
              <p className="text-foreground text-base font-semibold">
                <Trans>Switch to {confirmName}?</Trans>
              </p>
              <p className="text-muted-foreground text-sm">
                {sub.billedByStripe === true ? (
                  <Trans>
                    You keep {currentPlanName} until {periodEnd}. From then on you pay {confirmName}
                    .
                  </Trans>
                ) : sub.billedByStripe === false ? (
                  <Trans>
                    The change applies now; the unused part of {currentPlanName} is credited to your
                    subscription balance.
                  </Trans>
                ) : (
                  <Trans>
                    Your plan changes to {confirmName}. The date is confirmed when you save.
                  </Trans>
                )}
              </p>
              <div className="bg-muted/30 flex items-center justify-between rounded-xl border p-4 text-sm">
                <span className="text-muted-foreground">{confirmName}</span>
                <span className="font-semibold tabular-nums">
                  {money(tierIntervalTotal(confirmTier, sub.quantity, interval))}
                  {interval === "annual" ? <Trans>/year</Trans> : <Trans>/month</Trans>}
                </span>
              </div>
            </div>
          ) : tiersQ.isLoading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-28 w-full rounded-xl" />
              ))}
            </div>
          ) : tiersQ.isError ? (
            <LoadErrorState
              title={<Trans>Could not load the plans.</Trans>}
              onRetry={() => tiersQ.refetch()}
            />
          ) : plans.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center text-sm">
              <Trans>No other plans are available right now.</Trans>
            </p>
          ) : (
            <>
              {scheduledLock && (
                <p className="text-muted-foreground text-xs">
                  <Trans>Keep {currentPlanName} first to move to a higher plan.</Trans>
                </p>
              )}
              {plans.map((tier) => {
                const total = tierIntervalTotal(tier, sub.quantity, interval);
                const isCurrent = isCurrentTier(tier, sub);
                const isUpgrade = !isCurrent && total > currentTotal;
                const isScheduled = !!scheduledCode && tier.planCode === scheduledCode;
                const locked = scheduledLock && !isCurrent && total >= currentTotal;
                return (
                  <button
                    key={tier.planCode}
                    type="button"
                    disabled={isCurrent || locked || change.isPending}
                    onClick={() => pick(tier, total)}
                    className={cn(
                      "bg-card w-full rounded-xl border p-4 text-left transition-colors",
                      isCurrent
                        ? "border-primary/40 bg-primary-soft cursor-default"
                        : locked
                          ? "cursor-not-allowed opacity-60"
                          : "hover:border-primary/40 hover:bg-muted/40 active:scale-[0.99]",
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-foreground text-base font-semibold">
                            {tier.name}
                          </span>
                          {isCurrent && (
                            <Badge variant="default">
                              <Trans>Current plan</Trans>
                            </Badge>
                          )}
                          {isScheduled && (
                            <Badge variant="info">
                              <Trans>Scheduled</Trans>
                            </Badge>
                          )}
                        </div>
                        <p className="text-foreground mt-1 text-sm font-medium tabular-nums">
                          {money(total)}
                          {interval === "annual" ? <Trans>/year</Trans> : <Trans>/month</Trans>}
                        </p>
                      </div>
                      {!isCurrent && !locked && (
                        <span className="text-primary flex min-h-[44px] shrink-0 items-center gap-1 text-sm font-medium">
                          {isUpgrade ? <Trans>Upgrade</Trans> : <Trans>Downgrade</Trans>}
                          <ChevronRight className="size-4" aria-hidden />
                        </span>
                      )}
                    </div>
                    {tier.features.length > 0 && (
                      <ul className="mt-3 space-y-1.5">
                        {tier.features.map((feature) => (
                          <li
                            key={feature}
                            className="text-muted-foreground flex items-start gap-2 text-xs"
                          >
                            <Check className="text-primary mt-0.5 size-3.5 shrink-0" aria-hidden />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </button>
                );
              })}
            </>
          )}

          {error && (
            <div className="border-destructive/30 bg-destructive-soft text-destructive-strong rounded-xl border p-3 text-xs">
              {error}
              {servicesPaused && onShowServicePause && (
                <div className="mt-2">
                  <GoToServicePauseButton onClick={onShowServicePause} />
                </div>
              )}
            </div>
          )}
        </div>
      </ResponsiveSheet>
    </>
  );
}

// ---------------------------------------------------------------------------
// ScheduledChangeNotice — "Changes to X on <date>" + "Keep <current plan>"
// ---------------------------------------------------------------------------

export function ScheduledChangeNotice({
  sub,
  currentPlanName,
  onChanged,
  className,
}: {
  sub: ChangePlanSubscription;
  currentPlanName: string;
  onChanged: () => void;
  className?: string;
}) {
  const { t, i18n } = useLingui();
  const formatDate = useFormatDate();
  const errorCopy = useChangePlanErrorCopy();
  const { runProtected, PinGateElement } = useProtectedAction(["billing", "wallet"]);

  // Sending the CURRENT plan code cancels the scheduled change.
  const keep = useMutation({
    mutationFn: () =>
      runProtected(() =>
        apiFetch.post<ChangePlanResponse>(CHANGE_PLAN_PATH, {
          subscriptionId: sub.id,
          newPlanCode: sub.planTier,
        }),
      ),
  });

  const scheduled = sub.scheduledChange;
  if (!scheduled || sub.cancelledAt) return null;

  const newPlanName =
    (typeof scheduled.planName === "string"
      ? scheduled.planName
      : tLocalized(scheduled.planName as LocalizedString | null, i18n.locale)) ||
    scheduled.planCode.charAt(0).toUpperCase() + scheduled.planCode.slice(1);
  const date = formatDate(scheduled.effectiveAt);

  const onKeep = async () => {
    const handle = showToast(t`Saving…`, "loading");
    try {
      await keep.mutateAsync();
      handle.update(t`Saved — you keep ${currentPlanName}.`, "success");
      onChanged();
    } catch (err) {
      if (isVaultUnlockCancelled(err)) {
        handle.dismiss();
        return;
      }
      handle.update(errorCopy(err), "error");
    }
  };

  return (
    <div
      className={cn(
        "border-info-border bg-info-soft flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3",
        className,
      )}
    >
      {PinGateElement}
      <div className="flex min-w-0 items-start gap-2">
        <CalendarClock className="text-info-strong mt-0.5 size-4 shrink-0" aria-hidden />
        <p className="text-info-strong text-sm font-medium">
          <Trans>
            Changes to {newPlanName} on {date}
          </Trans>
        </p>
      </div>
      <Button variant="outline" size="touch" onClick={onKeep} disabled={keep.isPending}>
        {keep.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
        <Trans>Keep {currentPlanName}</Trans>
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FreePlanCard — the plan a doctor is on when he pays for nothing
// ---------------------------------------------------------------------------

export function FreePlanCard({
  entityType,
  onSeePaidPlans,
  fallback,
}: {
  entityType: string;
  onSeePaidPlans: () => void;
  /** Rendered when the entity type has no free plan (or the plans fail to load). */
  fallback: ReactNode;
}) {
  const tiersQ = usePricingTiers(entityType, true);

  if (tiersQ.isLoading) return <Skeleton className="mt-4 h-40 w-full rounded-xl" />;

  const free = tiersQ.data?.tiers.find(
    (tier) => tier.priceMonthly === 0 && tier.visibility !== "coming_soon",
  );
  if (!free) return <>{fallback}</>;

  return (
    <div className="mt-4 rounded-xl border p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <div className="bg-primary-soft text-primary flex size-10 shrink-0 items-center justify-center rounded-lg">
          <Crown className="size-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-foreground text-base font-semibold">{free.name}</span>
            <Badge variant="default">
              <Trans>Current plan</Trans>
            </Badge>
          </div>
          {free.desc && <p className="text-muted-foreground mt-1 text-sm">{free.desc}</p>}
        </div>
      </div>
      {free.features.length > 0 && (
        <ul className="mt-4 space-y-1.5">
          {free.features.map((feature) => (
            <li key={feature} className="text-muted-foreground flex items-start gap-2 text-sm">
              <Check className="text-primary mt-0.5 size-4 shrink-0" aria-hidden />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      )}
      <Button size="touch" className="mt-4 w-full sm:w-auto" onClick={onSeePaidPlans}>
        <Trans>See paid plans</Trans>
        <ChevronRight className="size-4" aria-hidden />
      </Button>
    </div>
  );
}
